import { Link } from "react-router-dom";
import { getFeaturedCases } from "@/data/featuredCases";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useGsapReveal } from "@/hooks/useGsapReveal";
import { useCarousel } from "@/hooks/useCarousel";
import "./FeaturedCases.css";
import { useLocale } from "@/i18n/useLocale";
import ClientLogo from "@/components/ClientLogo/ClientLogo";

/**
 * Casos de exito destacados, arriba de todo.
 *
 * Sigue el patron que Infosys usa en su home ("Top Stories" es su seccion #2,
 * apenas debajo del hero): lo primero despues de la promesa es la prueba.
 *
 * La seccion es clara, entre el hero negro y el ambar de e-commerce: el salto
 * de valor es lo que la hace destacar. Ademas resuelve los logos, que son las
 * variantes oscuras de cada marca y sobre blanco se leen solos.
 */
function FeaturedCases() {
  const { t, locale } = useLocale();
  const featuredCases = getFeaturedCases(locale);
  const headerRef = useScrollReveal<HTMLDivElement>();
  const revealRef = useGsapReveal<HTMLDivElement>({
    selector: ".case-card",
    stagger: 0.06,
    deps: [locale],
  });
  const {
    ref: trackRef,
    atStart,
    atEnd,
    scrollByCard,
  } = useCarousel<HTMLDivElement>(".case-card");

  return (
    <section className="featured-cases" id="casos" aria-labelledby="casos-titulo">
      <div className="featured-cases__content">
        <div className="featured-cases__header" ref={headerRef}>
          <div>
            <p className="featured-cases__eyebrow">{t.featuredCases.eyebrow}</p>
            <h2 className="featured-cases__title" id="casos-titulo">
              {t.featuredCases.title}
            </h2>
            <p className="featured-cases__lead">{t.featuredCases.lead}</p>
          </div>

          <div className="featured-cases__actions">
            <button
              type="button"
              className="featured-cases__arrow"
              onClick={() => scrollByCard(-1)}
              disabled={atStart}
              aria-label={t.news.prev}
            >
              ←
            </button>
            <button
              type="button"
              className="featured-cases__arrow"
              onClick={() => scrollByCard(1)}
              disabled={atEnd}
              aria-label={t.news.next}
            >
              →
            </button>
          </div>
        </div>

        <div ref={revealRef}>
          <div
            className="featured-cases__track"
            ref={trackRef}
            tabIndex={0}
            role="region"
            aria-label={t.featuredCases.eyebrow}
          >
          {featuredCases.map((c) => (
            <Link key={c.id} to={c.href} className="case-card">
              <div className="case-card__logo-wrap">
                {c.logo && c.brand ? (
                  <ClientLogo
                    src={c.logo}
                    alt={c.logoAlt ?? c.client}
                    brand={c.brand}
                    badge={c.logoBadge}
                    baseClass="case-card__logo"
                    badgeClass="case-card__logo-badge"
                  />
                ) : (
                  // Sin asset del logo: el nombre del cliente en tipografia.
                  // Es preferible a dejar el hueco o a poner un placeholder.
                  <span className="case-card__client">{c.client}</span>
                )}
              </div>

              <span className="case-card__tag">{c.category}</span>
              <p className="case-card__summary">{c.summary}</p>

              <span className="case-card__cta" aria-hidden="true">
                {t.featuredCases.cta}
              </span>
            </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FeaturedCases;
